import { AxiosError } from 'axios'
import React, { FunctionComponent, useEffect } from 'react'

import { BookmarkToggleButton } from './BookmarkToggleButton'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { useAppContext } from '../../context/app'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import {
  createConfirmationEvent,
  clearConfirmationEvent
} from '../../events/ConfirmationEvent'
import { BookmarkStatus } from '../../pages/Bookmarks/Bookmarks.model'
import { AnalyticsEvent } from '../../services/Analytics'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  createBookmark,
  getItemBookmarkStatus,
  deleteBookmark,
  AuthError,
  cancelCreateBookmark,
  cancelDeleteBookmark,
  getCancelGetLastItemBookmarkStatus
} from '../../services/ApiService'
import { BookmarkableItem } from '../../types/BookmarkableItem'
import { BookmarkedConfirmation } from '../BookmarkedConfirmation'

const confirmationLocalStorageKey = 'bookmarkConfirmationShownTo'

export const BookmarkToggleButtonContainer: FunctionComponent<
  BookmarkableItem & { iconOnly?: boolean; iconFirst?: boolean }
> = ({ itemId, itemType, iconOnly = false, iconFirst = false }) => {
  const { dispatch, user } = useAppContext()

  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [bookmarkId, setBookmarkId] = React.useState<string | null | undefined>(
    undefined
  )
  const [isHover, setIsHover] = React.useState<boolean>(false)
  const [bookmarksBtnMenu, setBookmarksBtnMenu] =
    React.useState<HTMLElement | null>()

  useEffect(() => {
    getItemBookmarkStatus({ itemId, itemType })
      .then((bookmark) => {
        setBookmarkId(bookmark && bookmark.bookmarkId)
        setBookmarksBtnMenu(document.getElementById('bookmarks')!)
        setIsLoading(false)
      })
      .catch((error: AxiosError) => {
        dispatch(
          createAnnounceEvent(
            AnnounceMode.Error,
            `There was an error loading your bookmark. ${error.message} ${
              error.code ? error.code : ''
            }`
          )
        )
      })
    return getCancelGetLastItemBookmarkStatus()
  }, [itemId, itemType, dispatch])

  const bookmarkedStatus = isLoading
    ? BookmarkStatus.Loading
    : bookmarkId
    ? BookmarkStatus.Bookmarked
    : BookmarkStatus.NotBookmarked

  const handleUpdateError = (error: AxiosError) => {
    // If request is cancelled continue
    if (error.message === AuthError.RequestCancelled) {
      return
    }
    dispatch(
      createAnnounceEvent(
        AnnounceMode.Error,
        `There was an error updating your bookmark. ${error.message} ${
          error.code ? error.code : ''
        }`
      )
    )
  }

  useEffect(() => {
    return () => {
      void cancelCreateBookmark()
      void cancelDeleteBookmark()
    }
  }, [])

  return (
    <BookmarkToggleButton
      iconOnly={iconOnly}
      iconFirst={iconFirst}
      bookmarkedStatus={bookmarkedStatus}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      isHover={isHover}
      toggleBookmark={(e) => {
        e.stopPropagation()

        if (bookmarkedStatus === BookmarkStatus.Loading) {
          return
        }

        if (bookmarkedStatus === BookmarkStatus.NotBookmarked) {
          createBookmark({
            itemId,
            itemType
          })
            .then((response) => {
              setIsLoading(false)
              setBookmarkId(response.data.bookmarkId)

              const confirmationShownTo = (
                localStorage.getItem(confirmationLocalStorageKey) || ''
              )
                .split(',')
                .filter((sub) => !!sub)
              const subjectId = user!.profile.sub

              if (
                bookmarksBtnMenu &&
                !confirmationShownTo.includes(subjectId)
              ) {
                dispatch(
                  createConfirmationEvent({
                    children: (
                      <BookmarkedConfirmation
                        closeConfirmation={() =>
                          dispatch(clearConfirmationEvent())
                        }
                        bookmarksBtnMenu={bookmarksBtnMenu}
                      />
                    ),
                    removePadding: true,
                    autoHideAfterMs: 10 * 1000
                  })
                )
                confirmationShownTo.push(subjectId)
                localStorage.setItem(
                  confirmationLocalStorageKey,
                  confirmationShownTo.join(',')
                )
              }

              analyticsServiceSingleton.trackEvent(
                AnalyticsEvent.BookmarkAddEvent,
                {
                  bookmarkId: itemId,
                  itemType
                }
              )
            })
            .catch(handleUpdateError)
        } else {
          deleteBookmark(bookmarkId!)
            .then(() => {
              setIsLoading(false)
              setBookmarkId(null)
              analyticsServiceSingleton.trackEvent(
                AnalyticsEvent.BookmarkRemoveEvent,
                {
                  bookmarkId
                }
              )
            })
            .catch(handleUpdateError)
        }

        setIsLoading(true)
      }}
    />
  )
}
